import { format as datefnsFormat } from "date-fns";
import { enUS } from "date-fns/locale";
import { useState } from "react";
import clsx from "clsx";

const locales = { enUS };

export const mergeClasses = (...classes) => {
  return clsx(classes);
};

export const format = (date, formatStr = "PP") => {
  if (!date) return "";
 
  const currentTimeZoneTime = new Date(
    new Date(date) - new Date().getTimezoneOffset() * 60000
  );

  return datefnsFormat(currentTimeZoneTime, formatStr, {
    locale: locales[window.__localeId__],
  });
};

export const useAnimateHook = (animation = "", timeout = 2000) => {
  const [animateClass, setAnimateClass] = useState("animate__animated");

  const triggerAnimation = () => {
    setAnimateClass(`animate__animated ${animation}`);
    setTimeout(() => {
      setAnimateClass(animateClass.replace(animation, ""));
    }, timeout);
  };

  return [animateClass, triggerAnimation];
};

export function truncateNumber(val, decimals = 1) {
  if (isNaN(val) && typeof val === "string") return val;
  if (isNaN(val) || !String(val)) return "";
  let parts = val.toString().split(".");
  if (parts.length == 1) return val;
  return parseFloat(parts[0] + "." + parts[1].substring(0, decimals));
}

export const formatDuration = (duration) => {
  if (!duration) return "";
  const [hours, minutes] = duration.split(":");
  return (
    <>
      <b>{hours} </b>
      <span>hours</span> <b>{minutes}</b>
      <span> min</span>
    </>
  );
};

export const fetcher = (url) => fetch(url).then((res) => res.json());

export function debounce(fn, wait) {
  let timer = null;
  return function (...params) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...params);
    }, wait);
  };
}

export function getCoordinatesFromPolygonText(boundaries) {
  if (!boundaries || typeof boundaries !== 'string') return [];

  // Helper to parse a single polygon string
  function parsePolygonCoords(coordsStr) {
    return coordsStr.split(",").map(elt => {
      const split = elt.trim().split(" ");
      // Accept 2D or 3D coordinates (ignore Z)
      if (split.length < 2) {
        console.log("Skipping malformed coordinate:", elt);
        return null;
      }
      const lat = Number(split[1]);
      const lng = Number(split[0]);
      if (!isFinite(lat) || !isFinite(lng)) {
        console.log("Invalid lat/lng:", { lat, lng, raw: elt });
        return null;
      }
      return { lat, lng };
    }).filter(Boolean);
  }

  // MULTIPOLYGON
  if (boundaries.startsWith('MULTIPOLYGON')) {
    const polys = [];
    const inner = boundaries.replace(/^MULTIPOLYGON\s*\(\s*/i, '').replace(/\s*\)$/, '');
    const polyStrings = inner.split(/\)\s*,\s*\(/);
    for (let polyStr of polyStrings) {
      polyStr = polyStr.replace(/^\(+|\)+$/g, '');
      const coords = parsePolygonCoords(polyStr);
      if (coords.length) polys.push(coords);
    }
    return polys;
  }

  // POLYGON Z
  if (boundaries.startsWith('POLYGON Z')) {
    const match = boundaries.match(/POLYGON Z\s*\(\((.*)\)\)/i);
    if (!match) return [];
    const coords = parsePolygonCoords(match[1]);
    return coords.length ? [coords] : [];
  }

  // POLYGON
  if (boundaries.startsWith('POLYGON')) {
    const match = boundaries.match(/POLYGON\s*\(\((.*)\)\)/i);
    if (!match) return [];
    const coords = parsePolygonCoords(match[1]);
    return coords.length ? [coords] : [];
  }

  // Fallback: try to parse as a single polygon
  const re = /.*\(\((.*)\)\)/;
  const match = boundaries.match(re);
  if (!match) return [];
  const coords = parsePolygonCoords(match[1]);
  return coords.length ? [coords] : [];
}

function wordToNumbers(word) {
  return word.split("").map((char) => char.charCodeAt(0));
}

export function generateRandomColor(word) {
  const num = wordToNumbers(word).reduce((acc, curr) => acc + curr, 0);

  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = Math.abs(Number(num) * (i + 1) * 123456789) % 256;
    color += ("00" + value.toString(16)).substr(-2);
  }
  return color;
}

export function getPolygonCentroid(paths) {
  const points = Array.isArray(paths[0]) ? paths[0] : paths;
  let area = 0, x = 0, y = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = parseFloat(points[i].lat), yi = parseFloat(points[i].lng);
    const xj = parseFloat(points[j].lat), yj = parseFloat(points[j].lng);
    const f = xi * yj - xj * yi;
    area += f;
    x += (xi + xj) * f;
    y += (yi + yj) * f;
  }
  area *= 0.5;
  if (area === 0) {
    return points[0];
  }
  x /= (6 * area);
  y /= (6 * area);
  return { lat: x, lng: y };
}

export const inchesToFeet = (value) =>
  value === '' || value === null || value === undefined ? value : Number(value) / 12;

export const feetToInches = (value) =>
  value === '' || value === null || value === undefined ? value : Number(value) * 12;
